<template>
  <main class="physical-pending-page">
    <AppNavBar
      :title="t('card.pendingPhysicalCards')"
      :auto-back="false"
      @click-left="handleBack"
    >
      <template #right>
        <button
          type="button"
          class="pending-nav-contact"
          @click="router.push('/mine/about')"
        >
          <AppSvgIcon name="contack_icon" size="22" color="#000" />
        </button>
      </template>
    </AppNavBar>

    <section class="cards-card">
      <DesktopSwipe
        ref="swipeRef"
        v-if="cardCount > 0"
        :loop="true"
        :show-indicators="false"
        @change="onSwipeChange"
      >
        <van-swipe-item v-for="item in cardCount" :key="item">
          <article class="pending-card">
            <app-image
              :src="themeStore.image('img16', defaultCardImage)"
              alt=""
            />
            <span class="course-card-label"
              ><app-image
                class="card__logo"
                :src="themeStore.image('img15', logo)"
                alt=""
            /></span>
            <div class="pending-card__status">
              <span class="pending-card__status-dot" />
              <span>{{ t("card.pendingActivate") }}</span>
            </div>
          </article>
        </van-swipe-item>
      </DesktopSwipe>
      <div v-if="cardCount > 1" class="carousel-dots">
        <i
          v-for="item in cardCount"
          :key="item"
          :class="{ 'is-active': pendingCardIndex === item - 1 }"
          @click="swipeRef?.swipeTo?.(item - 1)"
        />
      </div>
      <van-empty
        v-if="cardCount === 0"
        :description="t('card.noPendingPhysicalCard')"
        image="search"
      />
    </section>

    <section class="notice-card">
      <div class="notice-card__title">
        <span class="notice-card__title-icon">
          <AppSvgIcon
            name="icon-12"
            size="16"
            color="var(--wp-color-brand-1)"
          />
        </span>
        <div data-strong>{{ t("card.physicalInstruction") }}</div>
      </div>
      <ul>
        <li>{{ t("card.pendingNotice1") }}</li>
        <li>{{ t("card.pendingNotice2") }}</li>
        <li>{{ t("card.pendingNotice3") }}</li>
      </ul>
    </section>

    <button type="button" class="pending-add-card" @click="goOpenPhysicalCard">
      <span class="pending-add-card__icon">
        <AppSvgIcon name="plus" size="40" color="var(--wp-color-brand-1)" />
      </span>
    </button>

    <van-button
      block
      round
      type="primary"
      class="pending-action pending-action--primary"
      @click="goActivatePhysicalCard"
    >
      <span class="pending-action__content">
        <app-image
          class="pending-action__primary-icon"
          src="@/assets/images/cardImage/card_icon.png"
          alt=""
        />
        <span>{{ t("wallet.activateCard") }}</span>
      </span>
    </van-button>
    <van-button
      block
      round
      plain
      type="primary"
      class="pending-action pending-action--support"
      @click="router.push('/mine/about')"
    >
      <span class="pending-action__content">
        <AppSvgIcon
          name="contack_icon"
          size="20"
          color="var(--wp-color-brand-1)"
        />
        <span>{{ t("card.contactSupport") }}</span>
      </span>
    </van-button>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { closeToast, showFailToast, showLoadingToast } from "vant";
import DesktopSwipe from "@/components/DesktopSwipe.vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { pcCardCreatePre } from "@/api/card";
import { useCardStore } from "@/stores/card";
import { useProfileStore } from "@/stores/profile";
import { useThemeStore } from "@/stores/theme";
import { safeBack } from "@/utils/navigation";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import defaultCardImage from "@/assets/images/SVG/img16.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const cardStore = useCardStore();
const profileStore = useProfileStore();
const themeStore = useThemeStore();
const cardCount = computed(() => cardStore.unActiveCount);
const pendingCardIndex = ref(0);
const swipeRef = ref(null);
const pageState = computed(() =>
  getRouteState(route, "physical-card-pending", {}),
);

function onSwipeChange(index) {
  pendingCardIndex.value = index;
}

function handleBack() {
  if (pageState.value.from === "my_cards") {
    safeBack(router, "/home/card");
    return;
  }

  router.replace("/home/card");
}

function goOpenPhysicalCard() {
  navigateWithState(router, "/wallet/open-card", {
    namespace: "card-open",
    payload: {
      type: "1",
      forceBackHome: true,
    },
  });
}

async function loadCount() {
  try {
    await cardStore.fetchUnActiveCount();
  } catch (error) {
    showFailToast(error.message || error.msg || t("common.retryLater"));
  }
}

async function goActivatePhysicalCard() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = await pcCardCreatePre({
      address: profileStore.platformAddress,
    });
    const holderStatus = Number(result?.holderStatus || 0);

    if (holderStatus === 3) {
      navigateWithState(router, "/wallet/physical-active", {
        namespace: "wallet-physical-active",
        payload: {
          email: result?.email || profileStore.currentAccount?.email || "",
        },
      });
      return;
    }

    if (
      holderStatus === 2 ||
      holderStatus === 4 ||
      (holderStatus === 1 && String(result?.reason || "").trim())
    ) {
      navigateWithState(router, "/card/kyc/result", {
        namespace: "card-kyc-result",
        payload: {
          holderStatus: String(holderStatus),
          reason: result?.reason || "",
          type: "1",
        },
      });
      return;
    }

    router.push("/card/kyc");
  } catch (error) {
    showFailToast(error.message || error.msg || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

onMounted(loadCount);
</script>

<style scoped lang="scss">
.physical-pending-page {
  min-height: var(--app-viewport-height);
  padding: 14px 14px 34px;
  background: #f7f7f7;
  position: relative;
}

.cards-card,
.notice-card {
  border-radius: 16px;
  background: #ffffff;
}

.cards-card {
  padding: 20px 16px;

  :deep(.desktop-swipe),
  :deep(.van-swipe),
  :deep(.van-swipe__track),
  :deep(.van-swipe-item) {
    height: auto;
  }
}

.pending-card {
  position: relative;
  aspect-ratio: 933 / 585;
  margin: 0 5px;
  line-height: 0;

  .app-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  > .course-card-label {
    position: absolute;
    top: 24px;
    left: 28px;
    color: #ffffff;
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }
}

.pending-card__status {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 0 14px 0 16px;
  background: #1e866e;
  color: #fff;
  font-size: 14px;
  line-height: 20px;
}

.pending-card__status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ffffff;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  height: 8px;
  margin-top: 8px;
  line-height: 0;

  i {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d9d9d9;
  }

  .is-active {
    background: var(--wp-color-theme);
  }
}

.notice-card {
  margin: 12px 0 10px;
  padding: 20px 16px;
  border: 1px solid var(--wp-color-button-2);
  background: var(--wp-color-card-3);
}

.notice-card__title {
  display: flex;
  align-items: center;
  gap: 8px;

  .notice-card__title-icon {
    display: inline-flex;
    width: 24px;
    height: 24px;
    align-items: center;
    justify-content: center;
    flex: none;
    border-radius: 50%;
    background: var(--wp-color-brand-6);
  }

  [data-strong] {
    color: #000000;
    font-size: 16px;
    font-weight: 400;
  }
}

.notice-card ul {
  padding: 0;
  margin: 12px 0 0;
  list-style: none;
  color: #666666;
  font-size: 13px;
  line-height: 20px;
}

.notice-card li {
  position: relative;
  padding-left: 11px;
}

.notice-card li::before {
  position: absolute;
  top: 7px;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--wp-color-theme);
  content: "";
}

.notice-card li + li {
  margin-top: 10px;
}

.pending-add-card {
  display: flex;
  width: 100%;
  justify-content: flex-end;
  margin-bottom: 12px;
  padding: 0;
  border: 0;
  background: transparent;
}

.pending-add-card__icon {
  display: inline-flex;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
}

.pending-action {
  height: 44px;
  margin-bottom: 12px;
}

.pending-action--primary {
  box-shadow: 0 4px 4px #cddcff;
}

.pending-action--support {
  border: 0;
  background: var(--wp-color-button-2);
}

.pending-action__content {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.pending-action__primary-icon {
  width: 20px;
  height: 20px;
}

.card__logo {
  position: absolute;
  top: 24px;
  left: 20px;
  width: 146px !important;
  height: auto !important;
}

.pending-nav-contact {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 2px;
  padding: 0;
  border: 0;
  background: transparent;
}
</style>
